import {
  BackgroundColor,
  ChangeEvent,
  FileEntryBM,
  PageContentAnimationOption,
  PageMaxWidth,
  WebsiteConfigBM,
} from '@bm-js/atlascloud-shared'
import { Tr } from '../utils/useTr'
import { TrKey } from './translations/trKey.cnst'

export const EDITOR_LEFT_BAR_WIDTH = 250
export const EDITOR_RIGHT_BAR_WIDTH = 300
export const EDITOR_TOP_BAR_HEIGHT = 50
export const EDITOR_CENTER_PADDING = 15
export const EDITOR_DRAG_AND_DROP_TIMEOUT = 100

export const reservedPageNames = [
  'nyheter',
  'kalender',
  'utbildningar',
  'mitt-konto',
]

interface LeftBarField {
  label: string
  desc?: string
  type: 'color' | 'text' | 'image' | 'textarea' | 'tel' | 'email' | 'select'
  options?: {
    value: string
    label: string
  }[]
  value: string | undefined | FileEntryBM
  name?: string
  id?: string
  onChange?: (e: ChangeEvent) => void
  setter?: (value: FileEntryBM) => void
  key: string
}
interface LeftBarGroup {
  title: string
  fields: LeftBarField[]
  key: string
}
interface LeftBarFieldProps {
  patchConfig: (config: Partial<WebsiteConfigBM>) => void
  websiteConfig: WebsiteConfigBM
  tr: Tr
}
type GroupGetter = (props: LeftBarFieldProps) => LeftBarGroup
type ConfigGetter = (props: LeftBarFieldProps) => LeftBarGroup[]

const defaultColorGroup: GroupGetter = ({ websiteConfig, patchConfig, tr }) => {
  const patchColor = (patch: Partial<WebsiteConfigBM['colors']>) => {
    patchConfig({
      colors: { ...websiteConfig.colors, ...patch },
    })
  }
  return {
    key: 'editorConfigLeftColor',
    title: tr(TrKey.editorLeftColorsTitle),
    fields: [
      {
        key: 'baseColor',
        label: tr(TrKey.editorLeftBaseColorLabel),
        desc: tr(TrKey.editorLeftBaseColorDescription),
        type: 'color',
        value: websiteConfig.colors.base,
        name: 'base',
        placeholder: tr(TrKey.editorLeftBaseColorPlaceholder),
        id: 'base',
        onChange: (e) => patchColor({ base: e.target.value }),
      },
      {
        key: 'accentColor',
        label: tr(TrKey.editorLeftAccentColorLabel),
        desc: tr(TrKey.editorLeftAccentColorDescription),
        type: 'color',
        value: websiteConfig.colors.accent,
        name: 'accent',
        placeholder: tr(TrKey.editorLeftAccentColorPlaceholder),
        id: 'accent',
        onChange: (e) => patchColor({ accent: e.target.value }),
      },
    ],
  }
}

const defaultLogoGroup: GroupGetter = ({ websiteConfig, patchConfig, tr }) => {
  return {
    key: 'editorConfigLeftLogo',
    title: tr(TrKey.editorLeftLogoTitle),
    fields: [
      {
        key: 'logoWide',
        type: 'image',
        label: tr(TrKey.editorLeftLogoWideLabel),
        desc: tr(TrKey.editorLeftLogoWideDescription),
        value: websiteConfig.logoWide,
        setter: (f) => patchConfig({ logoWide: f }),
      },
      {
        key: 'logoSquare',
        type: 'image',
        label: tr(TrKey.editorLeftLogoSquareLabel),
        desc: tr(TrKey.editorLeftLogoSquareDescription),
        value: websiteConfig.logoSquare,
        setter: (f) => patchConfig({ logoSquare: f }),
      },
    ],
  }
}

const defaultContactGroup: GroupGetter = ({
  websiteConfig,
  patchConfig,
  tr,
}) => {
  const handleContactInfoChange = (e: ChangeEvent) => {
    patchConfig({
      contactInfo: {
        ...websiteConfig.contactInfo,
        [e.target.name]: e.target.value,
      },
    })
  }

  return {
    key: 'editorConfigLeftContact',
    title: tr(TrKey.editorLeftContactTitle),
    fields: [
      {
        key: 'recipient',
        type: 'textarea',
        value: websiteConfig.contactInfo.recipient,
        onChange: handleContactInfoChange,
        label: tr(TrKey.editorLeftContactRecipientLabel),
        name: 'recipient',
        id: 'recipient',
      },
      {
        key: 'adress',
        type: 'textarea',
        value: websiteConfig.contactInfo.adress,
        onChange: handleContactInfoChange,
        label: tr(TrKey.editorLeftContactAdressLabel),
        name: 'adress',
        id: 'adress',
      },
      {
        key: 'postalCode',
        type: 'text',
        value: websiteConfig.contactInfo.postalCode,
        onChange: handleContactInfoChange,
        label: tr(TrKey.editorLeftContactPostalCodeLabel),
        name: 'postalCode',
        id: 'postalCode',
      },
      {
        key: 'postalArea',
        type: 'text',
        value: websiteConfig.contactInfo.postalArea,
        onChange: handleContactInfoChange,
        label: tr(TrKey.editorLeftContactPostalAreaLabel),
        name: 'postalArea',
        id: 'postalArea',
      },
      {
        key: 'phone',
        type: 'tel',
        value: websiteConfig.contactInfo.phone,
        onChange: handleContactInfoChange,
        label: tr(TrKey.editorLeftContactPhoneLabel),
        name: 'phone',
        id: 'phone',
      },
      {
        key: 'email',
        type: 'email',
        value: websiteConfig.contactInfo.email,
        onChange: handleContactInfoChange,
        label: tr(TrKey.editorLeftContactEmailLabel),
        name: 'email',
        id: 'email',
      },
    ],
  }
}

const nav3ColorGroup: GroupGetter = (props) => {
  const defaultGroup = defaultColorGroup(props)

  const { websiteConfig, patchConfig, tr } = props
  const { navBackground } = websiteConfig

  const extraFields: LeftBarField[] = [
    {
      type: 'select',
      name: 'navBackground',
      key: 'navBackground',
      id: 'navBackground',
      label: tr(TrKey.editorLeftNavBackgroundLabel),
      desc: tr(TrKey.editorLeftNavBackgroundDescription),
      value: navBackground || 'bg-defaultBgGray',
      onChange: (e) =>
        patchConfig({ navBackground: e.target.value as BackgroundColor }),
      options: [
        {
          value: 'bg-defaultBgGray',
          label: tr(TrKey.editorBackgroundColorOptionStandard),
        },
        {
          value: 'bg-white',
          label: tr(TrKey.editorBackgroundColorOptionWhite),
        },
        {
          value: 'bg-offWhite',
          label: tr(TrKey.editorBackgroundColorOptionOffWhite),
        },
      ],
    },
  ]

  const fields = [...defaultGroup.fields, ...extraFields]

  return {
    ...defaultGroup,
    fields,
  }
}

const defaultSocialGroup: GroupGetter = ({
  websiteConfig,
  patchConfig,
  tr,
}) => {
  const handleSocialMediaChange = (e: ChangeEvent) => {
    patchConfig({
      socialMedia: {
        ...websiteConfig.socialMedia,
        [e.target.name]: e.target.value,
      },
    })
  }

  return {
    key: 'editorConfigLeftSocial',
    title: tr(TrKey.editorLeftSocialTitle),
    fields: [
      {
        key: 'instagram',
        type: 'text',
        value: websiteConfig.socialMedia.instagram,
        onChange: handleSocialMediaChange,
        label: 'Instagram',
        name: 'instagram',
        id: 'instagram',
      },
      {
        key: 'facebook',
        type: 'text',
        value: websiteConfig.socialMedia.facebook,
        onChange: handleSocialMediaChange,
        label: 'Facebook',
        name: 'facebook',
        id: 'facebook',
      },
    ],
  }
}

const footer2ContactGroup: GroupGetter = (props) => {
  const { websiteConfig, patchConfig, tr } = props

  const defaultGroup = defaultContactGroup(props)

  const fieldsToRemove = ['phone']

  const extraFields: LeftBarField[] = [
    {
      key: 'footerText',
      type: 'textarea',
      value: (websiteConfig.footerText || []).join('\n'),
      onChange: (e) => patchConfig({ footerText: e.target.value.split('\n') }),
      label: tr(TrKey.editorLeftContactFooterTextLabel),
      name: 'footerText',
      id: 'footerText',
    },
    {
      key: 'footerImage',
      type: 'image',
      value: websiteConfig.footerImage,
      label: tr(TrKey.editorLeftContactFooterImageLabel),
      setter: (f) => patchConfig({ footerImage: f }),
    },
  ]

  const filteredFields = defaultGroup.fields.filter(
    (f) => !fieldsToRemove.includes(f.key)
  )

  const fields = [
    ...extraFields,
    ...filteredFields.map((field) => ({ ...field, type: 'text' })),
  ] as LeftBarField[]

  return {
    ...defaultGroup,
    fields,
  }
}

const footer2SocialGroup: GroupGetter = (props) => {
  const { websiteConfig, patchConfig } = props

  const handleSocialMediaChange = (e: ChangeEvent) => {
    patchConfig({
      socialMedia: {
        ...websiteConfig.socialMedia,
        [e.target.name]: e.target.value,
      },
    })
  }

  const defaultGroup = defaultSocialGroup(props)

  const extraFields: LeftBarField[] = [
    {
      key: 'twitter',
      type: 'text',
      value: websiteConfig.socialMedia.twitter,
      onChange: handleSocialMediaChange,
      label: 'X/Twitter',
      name: 'twitter',
      id: 'twitter',
    },
  ]

  const fields = [...defaultGroup.fields, ...extraFields]

  return {
    ...defaultGroup,
    fields,
  }
}

const defaultPageConfigGroup: GroupGetter = ({
  websiteConfig,
  patchConfig,
  tr,
}) => {
  return {
    key: 'editorConfigLeftPageConfig',
    title: tr(TrKey.editorLeftPageConfigTitle),
    fields: [
      {
        type: 'select',
        name: 'websiteBackground',
        key: 'websiteBackground',
        id: 'websiteBackground',
        label: tr(TrKey.editorLeftPageConfigWebsiteBackgroundLabel),
        desc: tr(TrKey.editorLeftPageConfigWebsiteBackgroundDescription),
        value: websiteConfig.websiteBackground || 'bg-defaultBgGray',
        onChange: (e) =>
          patchConfig({
            websiteBackground: e.target.value as BackgroundColor,
          }),
        options: [
          {
            value: 'bg-defaultBgGray',
            label: tr(TrKey.editorBackgroundColorOptionStandard),
          },
          {
            value: 'bg-white',
            label: tr(TrKey.editorBackgroundColorOptionWhite),
          },
          {
            value: 'bg-offWhite',
            label: tr(TrKey.editorBackgroundColorOptionOffWhite),
          },
        ],
      },
      {
        type: 'select',
        name: 'pageMaxWidth',
        key: 'pageMaxWidth',
        id: 'pageMaxWidth',
        label: tr(TrKey.editorLeftPageConfigPageMaxWidthLabel),
        desc: tr(TrKey.editorLeftPageConfigPageMaxWidthDescription),
        value: websiteConfig.pageMaxWidth || 'normal',
        onChange: (e) =>
          patchConfig({ pageMaxWidth: e.target.value as PageMaxWidth }),
        options: [
          {
            value: 'normal',
            label: tr(TrKey.editorLeftPageConfigPageMaxWidthOptionNormal),
          },
          {
            value: 'wide',
            label: tr(TrKey.editorLeftPageConfigPageMaxWidthOptionWide),
          },
        ],
      },
      {
        type: 'select',
        name: 'pageContentAnimation',
        key: 'pageContentAnimation',
        id: 'pageContentAnimation',
        label: tr(TrKey.editorLeftPageConfigPageContentAnimationLabel),
        desc: tr(TrKey.editorLeftPageConfigPageContentAnimationDescription),
        value: websiteConfig.pageContentAnimation || 'none',
        onChange: (e) =>
          patchConfig({
            pageContentAnimation: e.target.value as PageContentAnimationOption,
          }),
        options: [
          {
            value: 'none',
            label: tr(TrKey.editorLeftPageConfigPageContentAnimationOptionNone),
          },
          {
            value: 'fade-bottom',
            label: tr(
              TrKey.editorLeftPageConfigPageContentAnimationOptionFadeBottom
            ),
          },
        ],
      },
    ],
  }
}

export const leftBarConfig: ConfigGetter = (props) => {
  const { websiteConfig } = props

  const contactGroup =
    websiteConfig.footerType === 'footer2'
      ? footer2ContactGroup(props)
      : defaultContactGroup(props)

  const socialGroup =
    websiteConfig.footerType === 'footer2'
      ? footer2SocialGroup(props)
      : defaultSocialGroup(props)

  const colorGroup =
    websiteConfig.navType === 'nav3'
      ? nav3ColorGroup(props)
      : defaultColorGroup(props)

  return [
    colorGroup,
    defaultLogoGroup(props),
    contactGroup,
    socialGroup,
    defaultPageConfigGroup(props),
  ]
}
